import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLifeEvents } from "@/hooks/useLifeEvents";
import { useProfileNotifications } from "@/hooks/useProfileNotifications";
import LoginSecurityPanel from "@/components/account/LoginSecurityPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ChevronRight, Pencil, Check, Info,
  Users, BarChart3, TrendingUp, Shield, Heart, Award, UserCircle2, AtSign, Loader2, X,
} from "lucide-react";
import PageShell from "@/components/premium/PageShell";
import PageHeader from "@/components/premium/PageHeader";
import { validateUsername } from "@/lib/username";
import AudioSettingsCard from "@/components/profile/AudioSettingsCard";

const ProfileScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { outcomes } = useLifeEvents();
  const { rewardCount, friendCount } = useProfileNotifications();
  const [profileName, setProfileName] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [editingUsername, setEditingUsername] = useState(false);
  const [usernameDraft, setUsernameDraft] = useState("");
  const [usernameStatus, setUsernameStatus] = useState<"idle" | "checking" | "available" | "taken" | "invalid">("idle");
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [savingUsername, setSavingUsername] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("display_name, username").eq("id", user.id).maybeSingle().then(({ data }) => {
      if (data?.display_name) setProfileName(data.display_name);
      setUsername((data)?.username ?? null);
      setUsernameDraft((data)?.username ?? "");
    });
  }, [user]);

  // Debounced availability for username edit
  useEffect(() => {
    if (!editingUsername) return;
    setUsernameError(null);
    if (!usernameDraft) {
      setUsernameStatus("idle");
      return;
    }
    if (usernameDraft.toLowerCase() === (username ?? "").toLowerCase()) {
      setUsernameStatus("idle");
      return;
    }
    const v = validateUsername(usernameDraft);
    if (v.ok !== true) {
      setUsernameStatus("invalid");
      setUsernameError(v.message);
      return;
    }
    setUsernameStatus("checking");
    const t = setTimeout(async () => {
      const { data, error } = await supabase.rpc("is_username_available", {
        p_username: usernameDraft,
        p_user_id: user?.id ?? null,
      });
      if (error) {
        setUsernameStatus("idle");
      } else if (data) {
        setUsernameStatus("available");
      } else {
        setUsernameStatus("taken");
        setUsernameError("That username is already taken. Try another.");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [usernameDraft, editingUsername, user, username]);

  const saveUsername = async () => {
    if (!user) return;
    const v = validateUsername(usernameDraft);
    if (v.ok !== true) {
      toast.error(v.message);
      return;
    }
    if (usernameDraft.toLowerCase() !== (username ?? "").toLowerCase() && usernameStatus !== "available") {
      toast.error("Pick an available username.");
      return;
    }
    setSavingUsername(true);
    const { error } = await supabase
      .from("profiles")
      .update({ username: usernameDraft })
      .eq("id", user.id);
    setSavingUsername(false);
    if (error) {
      toast.error(/reserved/i.test(error.message) ? "That username is reserved." : "Failed to save username");
      return;
    }
    setUsername(usernameDraft);
    setEditingUsername(false);
    toast.success("Username saved!");
  };

  const saveDisplayName = async () => {
    if (!user || !profileName.trim()) return;
    setSavingName(true);
    const { error } = await supabase.from("profiles").update({ display_name: profileName.trim() }).eq("id", user.id);
    setSavingName(false);
    if (error) {
      toast.error("Failed to save display name");
    } else {
      toast.success("Display name saved!");
      setEditingName(false);
    }
  };

  const email = user?.email || "";
  const displayName = profileName || email.split("@")[0];
  const initials = displayName.slice(0, 2).toUpperCase();

  const stressHealthy = outcomes ? outcomes.stress_score < 50 : true;
  const resilienceHealthy = outcomes ? outcomes.resilience_score > 50 : true;
  const confidenceHealthy = outcomes ? outcomes.financial_confidence_score > 50 : true;

  return (
    <PageShell>
      <PageHeader
        title="Profile"
        subtitle="Manage your account, preferences, progress identity, and HeirWise settings."
        icon={UserCircle2}
      />

        {/* Profile header */}
        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-primary/10 border-2 border-primary/30 flex items-center justify-center shrink-0">
              <span className="font-heading font-bold text-lg sm:text-xl text-primary">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input value={profileName} onChange={(e) => setProfileName(e.target.value)} placeholder="Enter display name..." className="h-8 text-sm" maxLength={30} onKeyDown={(e) => e.key === "Enter" && saveDisplayName()} />
                  <Button size="sm" aria-label="Save display name" onClick={saveDisplayName} disabled={savingName || !profileName.trim()}>
                    <Check className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h2 className="font-heading font-bold text-lg sm:text-xl text-foreground truncate">{displayName}</h2>
                  <Button size="sm" variant="ghost" aria-label="Edit display name" className="h-6 w-6 p-0" onClick={() => setEditingName(true)}>
                    <Pencil className="w-3 h-3 text-muted-foreground" />
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground truncate">{email}</p>
            </div>
          </div>

          {/* Username row */}
          <div className="mt-4 pt-4 border-t border-border/40">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <AtSign className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Username</span>
              </div>
              {!editingUsername && (
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => { setEditingUsername(true); setUsernameDraft(username ?? ""); }}>
                  <Pencil className="w-3 h-3 mr-1" /> {username ? "Edit" : "Set"}
                </Button>
              )}
            </div>
            {editingUsername ? (
              <div className="mt-2 space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">@</span>
                    <Input
                      value={usernameDraft}
                      onChange={(e) => setUsernameDraft(e.target.value.replace(/\s/g, ""))}
                      placeholder="yourhandle"
                      maxLength={20}
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      className="h-9 pl-6 pr-8 text-sm"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
                      {usernameStatus === "checking" && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                      {usernameStatus === "available" && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                      {(usernameStatus === "taken" || usernameStatus === "invalid") && <X className="w-3.5 h-3.5 text-destructive" />}
                    </span>
                  </div>
                  <Button size="sm" aria-label="Save username" onClick={saveUsername} disabled={savingUsername}>
                    {savingUsername ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setEditingUsername(false); setUsernameDraft(username ?? ""); setUsernameError(null); }}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                <p className={usernameError ? "text-[10px] text-destructive" : "text-[10px] text-muted-foreground"}>
                  {usernameError ?? "Use 3–20 characters. Letters, numbers, underscores, and periods only."}
                </p>
              </div>
            ) : (
              <div className="mt-1.5">
                <p className="text-sm font-heading font-semibold text-foreground truncate">
                  {username ? `@${username}` : <span className="text-muted-foreground font-normal">Not set</span>}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Your username helps trusted friends find you for Community and Challenge Center.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick nav: Friends & Rewards */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => navigate("/friends")} className="glass-card p-4 flex items-center gap-3 hover:border-primary/30 transition-colors text-left relative">
            <div className="w-9 h-9 rounded-xl bg-muted/60 flex items-center justify-center border border-border/50 shrink-0">
              <Users className="w-4 h-4 text-foreground/90" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-heading font-semibold text-foreground">Friends</p>
              <p className="text-[10px] text-muted-foreground">Compare & challenge</p>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 ml-auto" />
            {friendCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                {friendCount > 9 ? "9+" : friendCount}
              </span>
            )}
          </button>

          <button onClick={() => navigate("/achievements")} className="glass-card p-4 flex items-center gap-3 hover:border-primary/30 transition-colors text-left relative">
            <div className="w-9 h-9 rounded-xl bg-muted/60 flex items-center justify-center border border-border/50 shrink-0">
              <Award className="w-4 h-4 text-foreground/90" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-heading font-semibold text-foreground">Rewards</p>
              <p className="text-[10px] text-muted-foreground">Progression & badges</p>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 ml-auto" />
            {rewardCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                {rewardCount > 9 ? "9+" : rewardCount}
              </span>
            )}
          </button>
        </div>

        <LoginSecurityPanel email={email} />

        <AudioSettingsCard />

        {/* Financial Resilience */}
        {outcomes && (
          <div className="glass-card p-4 sm:p-6 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-primary shrink-0" />
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Practical Money Skills</span>
              <Tooltip>
                <TooltipTrigger aria-label="What Practical Money Skills measures">
                  <Info className="w-3 h-3 text-muted-foreground" />
                </TooltipTrigger>

                <TooltipContent className="max-w-[260px]">
                  <p className="text-[10px]">How you handle real-world money events. Built from decisions you make across HeirWise simulators and Life Events.</p>
                </TooltipContent>
              </Tooltip>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <MiniScore icon={<Heart className="w-3 h-3" />} value={outcomes.stress_score} healthy={stressHealthy} label="Stress" />
              <MiniScore icon={<Shield className="w-3 h-3" />} value={outcomes.resilience_score} healthy={resilienceHealthy} label="Resilience" />
              <MiniScore icon={<TrendingUp className="w-3 h-3" />} value={outcomes.financial_confidence_score} healthy={confidenceHealthy} label="Mastery" />
            </div>

            <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-2 border-t border-border/50">
              <span>{outcomes.total_events_faced} events faced</span>
              <span>{outcomes.total_events_handled_well} handled well</span>
            </div>
          </div>
        )}
    </PageShell>
  );
};

function MiniScore({ icon, value, healthy, label }: { icon: React.ReactNode; value: number; healthy: boolean; label: string }) {
  return (
    <div className="text-center">
      <div className={cn(
        "w-6 h-6 mx-auto rounded-full flex items-center justify-center mb-0.5",
        healthy ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"
      )}>
        {icon}
      </div>
      <p className="text-sm font-bold text-foreground">{value}</p>
      <p className="text-[8px] text-muted-foreground">{label}</p>
    </div>
  );
}

export default ProfileScreen;
